// Notifications (arc 101 slice 09) — the user-facing delivery layer. The worker
// fans one platform event out to every recipient of an account, honoring each
// user's per-(kind × channel) preference matrix, and records one row per
// (user, channel) attempt. Read-only UI: a notification links into the
// dashboard, never an in-app mutation.
//
// Source: `docs/idea/notifications.md`, `09-dashboards-realtime-pwa.md`.
//
// Pure vocabulary + the preference-matrix resolution. No I/O — the DB row shape
// lives in `@developerz/db`; the fanout orchestration + channels live in the
// worker.

import { z } from 'zod';
import { AppRelativePathSchema } from '../value-objects/app-path';
import { AccountIdSchema } from '../value-objects/ids';
import type { Role } from './account';
import { defaultChannels, isLockedChannel } from './notification-delivery';

/**
 * The event kinds the fanout delivers — one per topic the worker watches
 * (`09-dashboards-realtime-pwa.md` § PWA + notifications). Text + Zod-at-read in
 * the DB (D3 no-pgEnum), same stance as `runner.status` / `tier`.
 */
export const NotificationKind = {
  /** Urgent: a security, licence or CLA escalation. A SUBSET of what the `escalations` table holds; every other category rides the kind `ESCALATION_CATEGORY_KIND` names. */
  Escalation: 'escalation',
  /** A run is waiting on an answer anyone on the team can supply: a raised blocker, a repo setup fault. */
  TaskBlocked: 'task_blocked',
  /** A pull request merged (satisfying / FYI). */
  PrMerged: 'pr_merged',
  /** A CI job finished with a failing conclusion. */
  CiFailed: 'ci_failed',
  /** A fleet box offline 30 minutes, then one per later ladder rung (#117). */
  BoxOffline: 'box_offline',
  /** An artifact share link was minted for the recipient (slice 08). */
  ShareLink: 'share_link',
  /** A bug scouted by the bug-scout janitor was filed to the QA queue (#1866). */
  BugScouted: 'bug_scouted',
  /**
   * The daily ops digest (#1900): dead keys, quiet boxes, stuck work — a
   * LEVEL-triggered re-statement of what is STILL broken today, not an edge.
   */
  OpsDigest: 'ops_digest',
  /**
   * An ask was DISMISSED as already covered — triage's `dupe-of` verdict
   * (`TriageVerdict`, `value-objects/triage-verdict.ts`), carrying the pointer to
   * the work that covers it (arc 101 slice 08 § B3).
   *
   * ITS OWN KIND rather than a reuse, and the reuse that was considered is
   * `escalation`: the shapes are opposite. An escalation says a human must ACT
   * and emails an owner or admin on a locked channel (`isLockedChannel`); this
   * says nobody needs to act because the work is already in flight. Sending it
   * as an escalation would put an un-silenceable "action needed" in front of
   * someone whose only correct response is to read a link. `pr_merged` is the closest in urgency and wrong
   * in subject — it is about work FINISHING, not about an ask being declined.
   *
   * The reason it exists at all: a `dupe-of` that reaches nobody is
   * byte-identical to the platform losing the request, which is how a user stops
   * trusting the queue.
   */
  AskDuplicate: 'ask_duplicate',
  /**
   * The backlog import read a repo's EXISTING open issues and filed them as asks
   * (arc 101 slice 12 step 5, `apps/worker/src/consumers/backlog-import.ts`): on
   * the repo's first enable, or on an operator re-run of an enabled repo (#5716).
   *
   * THE DISCLOSURE THE IMPORT OWES, and the reason it is a notification rather
   * than only an audit row: doc 12 requires that walk to be "opt-in or clearly
   * announced", and an audit row is a PULL — it announces to whoever already
   * thought to look. Nobody installs an App and then goes reading an audit tail
   * on the off-chance we touched their tracker. A maintainer who sees N asks
   * appear must be able to learn where they came from without asking us.
   *
   * ITS OWN KIND rather than a reuse. `ops_digest` was the tempting one and is
   * wrong in subject: the digest re-states what is STILL broken, level-triggered,
   * and this is an edge, one per walk, about work the platform ACCEPTED. `pr_merged` is
   * the right urgency and the wrong direction — it reports work finishing, this
   * reports work arriving. Folding it into either would make an unsubscribe from
   * an unrelated topic silence a disclosure.
   */
  BacklogImported: 'backlog_imported',
  /**
   * Coding work on a repo is HELD until its onboarding pass completes
   * (`docs/idea/repo-onboarding.md`) — the claim gate withholds every
   * `outcome_kind = 'pr'` task on a repo with no `repositories.ai_first_at`, and
   * until this kind existed nothing told the maintainer.
   *
   * THE DEFECT IT ANSWERS, measured on prod 2026-09-11: of nine onboarding passes,
   * SEVEN ended `blocked` (5 `no_checkout`, 1 `github_refused`, 1
   * `generator_failed`) and every one of them was an `info` log line and nothing
   * else. One repo's coding task had been held four days behind a `no_checkout`
   * nobody was told about; another held five tasks for eleven days while its
   * `dz/onboarding` pull request sat open with nothing naming it. The repositories
   * and that pull request are named in the 2026-09-11 `docs/progress.md` entry
   * (epic #3766), not here: a comment naming an open pull request is stale the day
   * it merges.
   *
   * ONE KIND, TWO FACES, and the reason it is one and not two:
   *
   *   `gaps`    "merge pull request #N to start the work" — the pass wrote a diff
   *             and its MERGE is what stamps the repo AI-first.
   *   `blocked` "the pass is blocked: <prerequisite> — <remedy>" — it named a
   *             prerequisite it could not satisfy, so it never looked.
   *
   * Both sentences answer the SAME question — *why is the coding work on this repo
   * not moving, and what do I do about it* — and they differ only in which step of
   * the one pass is owed. A maintainer who mutes "my repo is waiting on onboarding"
   * means both; splitting them would let an unsubscribe from the PR reminder
   * silence the blocked report, which is the face that carries the harder news.
   * It is also ONE hold on the row either way (`TaskHoldReason.RepoNotOnboarded`),
   * so two kinds would be two notifications for one stalled queue.
   *
   * ITS OWN KIND rather than a reuse, and the reuse considered is `task_blocked`:
   * that one means "the bot DRAFTED and is waiting for a human to approve an
   * action it has already decided on", and it routes `ask_before_acting.*`. This
   * one means the work never started and there is nothing to approve — sending it
   * as `task_blocked` would file it under approvals, where the remedy (merge a PR,
   * or grant the pass what it could not get) is not one of the buttons.
   *
   * CLEARED BY THE MARKER, not by a second event: the pass reaching `ready`, or
   * the `dz/onboarding` pull request merging, stamps `repositories.ai_first_at`,
   * and `planOnboardingWait` (`apps/worker/src/notifications/onboarding-wait.ts`)
   * emits nothing once it is stamped.
   */
  OnboardingWait: 'onboarding_wait',
  /**
   * An account's KEY OUTAGE — none of the keys in the org pool can serve its
   * held work right now — *"no key of mine can serve my held work right now,
   * and what do I do"* (epic #5099, #5121). Produced by the cron tick
   * (#5116)'s LEVEL-TRIGGERED owed-read (`listKeyOutageNoticesOwed`), which
   * emits `opened` once a `key_outages` row is open with no `opened` notice
   * yet, and `resolved` once it is closed with no `resolved` notice yet;
   * `update` does not re-emit — only the snapshot-CHANGED case edits an
   * in-place notice on the ops channel, the fanout path keeps a
   * once-per-(outage, phase) rule.
   *
   * ONE NOTICE PER (OUTAGE, PHASE), and the (id, phase) tuple is the
   * collapse key — see `keyOutageDedupeKey` in
   * `packages/domain/src/entities/key-outage-notice.ts`. The owed-read
   * `listKeyOutageNoticesOwed` (`packages/db/src/queries/key-outages.ts`)
   * reads `notifications` to refuse a re-emit for an (id, phase) the table
   * already carries.
   */
  KeyOutage: 'key_outage',
  /**
   * An account has NO org model key and has work held for want of one —
   * *"why is none of my held work running, and what do I do"* (arc 101 slice 07
   * class 4 / epic #5099, #5120).
   *
   * ITS OWN KIND, not a phase of {@link NotificationKind.KeyOutage}, and the
   * four reasons the distinction is load-bearing:
   *
   * - A keyless account is NOT an outage by owner ruling (epic #5099 § Decisions):
   *   with no key there is nothing to pick and nothing recovers by itself, so an
   *   outage banner would offer an empty picker. `accountHasOrgKeySql`
   *   (`packages/db/src/queries/key-outages.ts`) is the one seam that excludes
   *   such accounts from `key_outages`, and this kind is the complement of that
   *   fragment, so the two can never disagree about which surface an account
   *   falls under.
   *
   * - {@link NotificationKind.KeyOutage}'s payload carries an outage id and
   *   it pairs `opened` with `resolved`. A keyless account has no outage
   *   record, so it has no id to carry and no pick to offer.
   *
   * - The remedy is different: add a key, not pick one or wait for a cooldown.
   *
   * - A maintainer who mutes "tell me when my keys cannot serve work" should
   *   still hear "you have no keys at all" — collapsing them would let an
   *   opt-out from a throttle silence the face that has no remedy but to add a
   *   key.
   *
   * ONE NOTICE PER KEYLESS EPISODE, and the episode is `max(byok_keys.revoked_at)`
   * over the account's ORG keys — a structural fact (the only writers of
   * `revoked_at` are the two revoke writers and it is never cleared), so the
   * same read answers "have I already told this episode" with no separate
   * lifecycle row. The collapse key is `noModelKeysDedupeKey(accountId,
   * lastOrgKeyRevokedAt)`; see `apps/worker/src/notifications/no-model-keys.ts`.
   */
  NoModelKeys: 'no_model_keys',
  /**
   * Something is waiting for a decision from the people who run the account: an
   * approval the bot asked for, a pull request the platform stopped on, a request
   * triage handed to a person, a full queue or a seat limit. ONE kind for all of
   * them, so one switch covers "needs a decision". The enum only grows, so a
   * split needs no schema migration — but preferences are keyed on kind, so a
   * second kind carved out of this one owes a backfill of the stored overrides
   * before the two switches read true.
   */
  DecisionNeeded: 'decision_needed',
  /** Good news about a repo: a release shipped, a first contribution merged, a milestone, a streak. */
  GoodNews: 'good_news',
  /**
   * The platform recorded a problem of its OWN while working for the account
   * (its tooling, a failed tool call, expectation accounting). Its tier is
   * `hidden`: in this slice that means the default matrix row off on every
   * channel plus a `setPref` that refuses an override, and `classifyDelivery`
   * encodes the same rule. NOTHING emits it yet — and with every channel off a
   * fanout recipient contributes nothing
   * (`apps/worker/src/notifications/plan.ts`), so it would write no ledger row
   * and would not reach the in-app inbox until slice 06 (#5692) adds the
   * in-app anchor; slice 11b (#5696) re-points the fan-out onto
   * `classifyDelivery`.
   */
  PlatformNotice: 'platform_notice',
  /**
   * A repo's worker mode was AUTO-PAUSED after two or more of its bot pull requests
   * were reverted within 24 hours (#5647) — the platform held new coding on
   * that repo on its own, and a person must resume it.
   */
  CodingPaused: 'coding_paused',
  /**
   * A BOUND purpose route changed how it is served (#7810): its pinned model stopped
   * serving and the lane runs on a fallback rung (or on nothing), or it came back to
   * the pin. Produced by `cron.lane_health`, which compare-and-sets
   * `llm_lane_states` and fans out ONLY when the stored state moved, so it is one
   * notice per state CHANGE, never one per task or per tick
   * (`laneDegradedNotification`, `apps/worker/src/notifications/lane-degraded.ts`).
   */
  LaneDegraded: 'lane_degraded',
  /**
   * The weekly summary of what the bot handled, mailed by `cron.digest_weekly`
   * to each member whose email for this kind is on. Its own kind so one person
   * can switch it off without touching anyone else's (#5700). It never rides
   * the fan-out: the digest lane records the ledger row itself.
   */
  WeeklySummary: 'weekly_summary',
} as const;
export const NotificationKindSchema = z.enum(NotificationKind);
export type NotificationKind = z.infer<typeof NotificationKindSchema>;

/** Every kind, for iteration (matrix defaults, tests, the preferences UI). */
export const NOTIFICATION_KINDS = NotificationKindSchema.options;

/**
 * The groups a person reasons about, in place of one switch per kind. A tier
 * decides WHEN an email goes (at once, or in a bundle) and where a kind sits on
 * the Notifications page; whether it goes at all is the preference matrix's answer.
 */
export const NotificationTier = {
  /**
   * Security, licence, CLA. Sent at once; an owner's and an admin's email is
   * locked on (`isLockedChannel`), members and viewers are not emailed by
   * default (`defaultChannels`), and when NOBODY is reachable the reach rule
   * releases the plan's refused email cells to members with an address
   * (`urgentFallbackApplies`). The blocked-task email is locked too, for every
   * role — the second cell the lock covers (plan 101 03-10).
   */
  Urgent: 'urgent',
  /** Waiting on a person. Bundled at the recipient's cadence. */
  Decision: 'decision',
  /** Something is broken right now. Sent at once. */
  HeadsUp: 'heads_up',
  /** Things that happened. Bundled at the recipient's cadence when switched on. */
  Updates: 'updates',
  /** Periodic summaries. Each is its own email. */
  Summaries: 'summaries',
  /**
   * Not delivered on any channel and absent from the preference grid: the
   * default matrix row is off on every channel, and `setPref` refuses an
   * override (`{ ok: false, reason: 'hidden' }`, its own arm beside the
   * mandatory refusal's `'mandatory'`). The kind is still IN the
   * `prefs_set`/`prefs_clear` input enum — the refusal, not the schema, is what
   * keeps a stored cell out.
   */
  Hidden: 'hidden',
} as const;
export const NotificationTierSchema = z.enum(NotificationTier);
export type NotificationTier = z.infer<typeof NotificationTierSchema>;

/** Ordered as declared above; Hidden is last and never shown. */
export const NOTIFICATION_TIERS = NotificationTierSchema.options;

/** Every kind's tier. Total, so a kind added without a tier fails to compile. */
export const KIND_TIER: Record<NotificationKind, NotificationTier> = {
  [NotificationKind.Escalation]: NotificationTier.Urgent,
  [NotificationKind.DecisionNeeded]: NotificationTier.Decision,
  [NotificationKind.TaskBlocked]: NotificationTier.Decision,
  [NotificationKind.OnboardingWait]: NotificationTier.Decision,
  [NotificationKind.BoxOffline]: NotificationTier.HeadsUp,
  [NotificationKind.KeyOutage]: NotificationTier.HeadsUp,
  [NotificationKind.NoModelKeys]: NotificationTier.HeadsUp,
  [NotificationKind.ShareLink]: NotificationTier.HeadsUp,
  [NotificationKind.PrMerged]: NotificationTier.Updates,
  [NotificationKind.CiFailed]: NotificationTier.Updates,
  [NotificationKind.BugScouted]: NotificationTier.Updates,
  [NotificationKind.AskDuplicate]: NotificationTier.Updates,
  [NotificationKind.BacklogImported]: NotificationTier.Updates,
  [NotificationKind.GoodNews]: NotificationTier.Updates,
  [NotificationKind.OpsDigest]: NotificationTier.Summaries,
  [NotificationKind.PlatformNotice]: NotificationTier.Hidden,
  [NotificationKind.CodingPaused]: NotificationTier.HeadsUp,
  [NotificationKind.LaneDegraded]: NotificationTier.HeadsUp,
  [NotificationKind.WeeklySummary]: NotificationTier.Summaries,
};

/**
 * The kinds the preference surfaces list — every kind whose tier is not
 * {@link NotificationTier.Hidden}. The ONE spelling of the "not hidden" filter
 * for surfaces that LIST kinds: the preference matrix (`resolveNotificationMatrix`),
 * the dashboard's mock grid and the preset walk all iterate it, so a hidden kind
 * cannot reappear as a row on one surface only. The OTHER spellings of the test,
 * each its own site: `resolveCells` and `emailDecision` (both in
 * `entities/notification-delivery.ts` — every cell off, and the email `skip`), `setPref` (`apps/api/src/deps/notifications.ts` — the
 * `reason: 'hidden'` refusal) and the dashboard mock's PUT branch
 * (`apps/dashboard/src/lib/account-notifications-mocks.ts` — the 422).
 */
export const LISTED_NOTIFICATION_KINDS: readonly NotificationKind[] = NOTIFICATION_KINDS.filter(
  (kind) => KIND_TIER[kind] !== NotificationTier.Hidden,
);

/** How often bundled email goes to one person on one account. */
export const EmailCadence = { Instant: 'instant', Hourly: 'hourly', Daily: 'daily' } as const;
export const EmailCadenceSchema = z.enum(EmailCadence);
export type EmailCadence = z.infer<typeof EmailCadenceSchema>;

/** The cadence of a person who has chosen none. */
export const DEFAULT_EMAIL_CADENCE: EmailCadence = EmailCadence.Hourly;

/** The three one-click settings. `recommended` is the defaults: no override at all. */
export const NotificationPreset = {
  OnlyUrgent: 'only_urgent',
  Recommended: 'recommended',
  Everything: 'everything',
} as const;
export const NotificationPresetSchema = z.enum(NotificationPreset);
export type NotificationPreset = z.infer<typeof NotificationPresetSchema>;

/**
 * Delivery channels. `push` = Web Push to the user's PWA devices (deep-links in);
 * `email` = `@developerz/email` (SMTP); `webhook` = the account's outbound hooks
 * (slice 03). Text + Zod-at-read in the DB.
 */
export const NotificationChannel = {
  Push: 'push',
  Email: 'email',
  Webhook: 'webhook',
} as const;
export const NotificationChannelSchema = z.enum(NotificationChannel);
export type NotificationChannel = z.infer<typeof NotificationChannelSchema>;

/** Every channel, for iteration. */
export const NOTIFICATION_CHANNELS = NotificationChannelSchema.options;

/**
 * A recorded delivery's lifecycle. `pending` on insert (audit-first: the row
 * lands before the channel is touched), then `sent` on a 2xx, `failed` on a
 * transport error, or one of the TWO non-delivery terminals below.
 *
 * `suppressed` and `no_target` were one status until they were split, and the
 * split is the point: `suppressed` means SOMEONE CHOSE THIS (an account
 * unsubscribed from the category, a routed email already landed once for the
 * whole account) and `no_target` means NOBODY COULD RECEIVE IT (zero push
 * subscriptions, no address on file, no subscribed webhook, a channel the
 * operator never wired). The first is the system working; the second is the
 * reach plane being dark, and while both wrote `suppressed` the two were
 * byte-identical in the ledger. Measured on prod 2026-09-01: 23,380 `suppressed`
 * push rows, 0 sent, and `push_subscriptions` empty — every one of them the
 * second case, indistinguishable from 23,380 people having opted out.
 * `deferred` is the one waiting state besides `pending`: the row was recorded, the email was held for a bundle, and a different lane sends it.
 */
export const NotificationStatus = {
  Pending: 'pending',
  Sent: 'sent',
  Failed: 'failed',
  /** A human's choice: an opt-out, or a copy already delivered account-wide. */
  Suppressed: 'suppressed',
  /** Nothing to deliver TO — zero subscriptions / addresses / routes / channel. */
  NoTarget: 'no_target',
  /**
   * The email is waiting for the recipient's next bundle (`cron.email_bundle`). Not a
   * terminal: the bundle lane moves the row to `sent`, `suppressed`, `no_target` or
   * `failed`. Only an email row is ever `deferred`.
   */
  Deferred: 'deferred',
} as const;
export const NotificationStatusSchema = z.enum(NotificationStatus);
export type NotificationStatus = z.infer<typeof NotificationStatusSchema>;

/** Every status, for iteration (the DB CHECK guard, the preferences UI, tests). */
export const NOTIFICATION_STATUSES = NotificationStatusSchema.options;

/**
 * The two terminals a channel elects between when it did NOT deliver and did NOT
 * error. Its own type rather than the full `NotificationStatus` so the refusal
 * seam cannot be handed `sent` / `pending` / `failed`: a channel reports what it
 * SAW, and the three other statuses are decided elsewhere (a 2xx, an insert, a
 * throw). One seam, two legal values, checked by the compiler.
 */
export const NotificationNonDeliverySchema = z.enum([
  NotificationStatus.Suppressed,
  NotificationStatus.NoTarget,
]);
export type NotificationNonDelivery = z.infer<typeof NotificationNonDeliverySchema>;

/**
 * The normalized event a producer enqueues (`notify.fanout`). `deepLink` is a
 * PATH into the PWA (leading `/`) — the push payload carries it verbatim for the
 * service worker's `notificationclick`, and the email wiring prepends the app
 * origin. `dedupeKey` (optional) is the BullMQ jobId, so a burst of the same
 * event collapses to one fanout. `payload` is opaque context persisted on each
 * row (entity ids, run id) for the inbox + downstream rendering.
 */
export const NotificationEventSchema = z.object({
  accountId: AccountIdSchema,
  kind: NotificationKindSchema,
  /** One-line headline — the push/email title. */
  title: z.string().min(1).max(200),
  /** Short body — the push/email preview line. */
  body: z.string().min(1).max(1000),
  /**
   * In-app path the notification opens; must be app-relative (leading `/`). The
   * rule is SHARED with the push payload's `url` (`value-objects/app-path.ts`) —
   * one definition, so the stored link and the pushed link can't disagree.
   */
  deepLink: AppRelativePathSchema,
  /** Stable collapse key — the fanout jobId; a re-emit dedupes. */
  dedupeKey: z.string().min(1).max(200).optional(),
  /** Opaque context persisted per row (entity ids, run id, …). */
  payload: z.record(z.string(), z.unknown()).optional(),
});
export type NotificationEvent = z.infer<typeof NotificationEventSchema>;

/** One per-(kind, channel) user override — absence means "use the default". */
export const NotificationPreferenceSchema = z.object({
  kind: NotificationKindSchema,
  channel: NotificationChannelSchema,
  enabled: z.boolean(),
});
export type NotificationPreference = z.infer<typeof NotificationPreferenceSchema>;

type ChannelMatrix = Record<NotificationChannel, boolean>;

/**
 * The default preference matrix (kind × channel) applied when a user has set no
 * override. Push is the primary channel for every kind but `share_link` (push
 * off — the email carries the link). Email doubles up only on
 * what needs a person or is broken right now (escalation, blocked-awaiting-approval,
 * a box going dark, a held queue, a key outage, an account with no model keys at
 * all) and carries the low-urgency share
 * link. Updates (`pr_merged`, `ci_failed`, `bug_scouted`, `ask_duplicate`,
 * `backlog_imported`) and the daily `ops_digest` do NOT email by default: they
 * land in the inbox and the browser, and a user who wants the email switches the
 * cell on. Webhook is opt-in only — account-scoped hooks (slice 03) are off by
 * default so a personal notification never surprises a shared integration.
 * Source: `docs/idea/notifications.md`.
 */
export const DEFAULT_NOTIFICATION_MATRIX: Record<NotificationKind, ChannelMatrix> = {
  [NotificationKind.Escalation]: { push: true, email: true, webhook: false },
  [NotificationKind.TaskBlocked]: { push: true, email: true, webhook: false },
  [NotificationKind.PrMerged]: { push: true, email: false, webhook: false },
  [NotificationKind.CiFailed]: { push: true, email: false, webhook: false },
  [NotificationKind.BoxOffline]: { push: true, email: true, webhook: false },
  [NotificationKind.ShareLink]: { push: false, email: true, webhook: false },
  [NotificationKind.BugScouted]: { push: true, email: false, webhook: false },
  // NO EMAIL BY DEFAULT. It was `box_offline`'s row (#1900). A daily summary that
  // arrives unasked is the mail people learn to skip, and what it restates that
  // needs a person now (a box down, a key outage) emails on its own kind.
  [NotificationKind.OpsDigest]: { push: true, email: false, webhook: false },
  // NO EMAIL BY DEFAULT. It answers a question a human asked, and the answer
  // still reaches them: the inbox row and the browser notification both carry
  // the link to the work that covers the ask. Reading that link is the only
  // response, so it does not interrupt an inbox. Fully user-controllable.
  [NotificationKind.AskDuplicate]: { push: true, email: false, webhook: false },
  // NO EMAIL BY DEFAULT. The import is still DISCLOSED: the notification lands
  // in the inbox and the browser, which is what makes it a push to the
  // maintainer rather than an audit row they must think to read. Fully
  // user-controllable.
  [NotificationKind.BacklogImported]: { push: true, email: false, webhook: false },
  // EMAILS BY DEFAULT, for the reason the kind's own docblock measures: the
  // alternative is what prod did for weeks — a queue held on a repo nobody was
  // told about, where a missed push is byte-identical to the platform having
  // silently dropped the work. Not a locked cell (`isLockedChannel`): a maintainer
  // who has decided to leave a repo un-onboarded is entitled to stop hearing
  // about it, and the hold is visible on the plan projection (`heldOn`) and in
  // `task stalls` either way.
  [NotificationKind.OnboardingWait]: { push: true, email: true, webhook: false },
  // EMAILS BY DEFAULT for the same reason the key rows above do — a missed push
  // is byte-identical to the platform having silently dropped the disclosure.
  // The posture mirrors the key rows beside it because the KIND differs on
  // identity (account outage vs keyless account) and producer (cron tick vs
  // keyless pass), not on its silence-vs-not tradeoff. NOT a locked cell
  // (`isLockedChannel`): a `picked` close sends the held work forward on its own, and
  // forcing an un-silenceable email for a single-pick recovery is how a person
  // learns to ignore the whole lane. The account is entitled to turn it off; it
  // is not entitled to have never been told.
  [NotificationKind.KeyOutage]: { push: true, email: true, webhook: false },
  // EMAILS BY DEFAULT for the same reason {@link NotificationKind.KeyOutage}'s
  // row above does — a missed push is byte-identical to the platform having
  // silently dropped the disclosure. NOT a locked cell (`isLockedChannel`): the
  // episode is held once per revoked-at, and a maintainer who has decided to
  // stay keyless is entitled to stop hearing about it.
  [NotificationKind.NoModelKeys]: { push: true, email: true, webhook: false },
  // EMAILS BY DEFAULT for owners and admins; members and viewers are not
  // emailed by default (`defaultChannels` in `notification-delivery.ts`, folded
  // in by `resolveEnabledChannels` below). No cell is locked: the account may
  // switch it off.
  [NotificationKind.DecisionNeeded]: { push: true, email: true, webhook: false },
  // Good news is an update: browser yes, email only for someone who asks for it.
  [NotificationKind.GoodNews]: { push: true, email: false, webhook: false },
  // Hidden tier: every channel off. Nothing emits the kind yet. A fanout
  // recipient still keeps one refused email cell (`planFanout`'s inbox anchor),
  // so the event reaches the in-app inbox when a producer starts emitting it.
  [NotificationKind.PlatformNotice]: { push: false, email: false, webhook: false },
  // EMAILS BY DEFAULT, the key rows' posture (see KeyOutage above): the
  // platform held one repo's new coding on its own, and a missed push is
  // byte-identical to the owner never learning their bot's PRs were reverted
  // repeatedly. NOT a locked cell (`isLockedChannel`) — a maintainer who prefers
  // the pause is entitled to stop hearing about it.
  [NotificationKind.CodingPaused]: { push: true, email: true, webhook: false },
  // EMAILS BY DEFAULT, the key rows' posture (KeyOutage above): a lane that left its
  // pinned model is spending on a different model, or running nothing, and the owner
  // who set dz up once is not watching the dashboard. One notice per state change.
  [NotificationKind.LaneDegraded]: { push: true, email: true, webhook: false },
  // Email only: a weekly recap is something to read, not something to be
  // interrupted by. On for every role, and switchable by every role.
  [NotificationKind.WeeklySummary]: { push: false, email: true, webhook: false },
};

/**
 * Structured vocabulary for "an escalation that could not be delivered" (#1633).
 * Fanout / cron lanes emit `level: 'alert'` lines carrying a `reason` from this
 * set so an operator grep finds one shape, not a free-form message. New kinds
 * are added here, not as inline strings.
 *
 * - `routes_empty` — `email_routes` is empty AND the account has no fallback
 *   `default_email`; the escalation is SUPPRESSED by the fanout's email
 *   channel because no destination exists.
 * - `channel_unwired` — the email port itself is absent (SMTP creds missing)
 *   AND no fallback channel is live.
 * - `transport_tls` — the email transport threw a TLS-specific error on
 *   delivery (handshake/cipher/cert); ongoing since 2026-08-10 13:01.
 * - `unknown` is the catch-all a port MUST name — a future shape (DNS, auth)
 *   lands here explicitly rather than drifting into prose.
 */
export const NotificationDeliveryFailure = {
  RoutesEmpty: 'routes_empty',
  ChannelUnwired: 'channel_unwired',
  TransportTls: 'transport_tls',
  Unknown: 'unknown',
} as const;
export type NotificationDeliveryFailure =
  (typeof NotificationDeliveryFailure)[keyof typeof NotificationDeliveryFailure];

/**
 * Resolve the set of channels a person receives `kind` on: THE preference matrix,
 * and the one fold the fan-out plan and the API grid both run. Start from the
 * role's default row (`defaultChannels`), apply the person's overrides in order,
 * then force the locked cells on (`isLockedChannel`: an override can never
 * silence one). Pure and total.
 */
export function resolveEnabledChannels(
  kind: NotificationKind,
  role: Role,
  overrides: Iterable<Pick<NotificationPreference, 'channel' | 'enabled'>>,
): Set<NotificationChannel> {
  const resolved: ChannelMatrix = { ...defaultChannels(kind, role) };
  for (const { channel, enabled } of overrides) {
    resolved[channel] = enabled;
  }
  return new Set(
    NOTIFICATION_CHANNELS.filter(
      (channel) => resolved[channel] || isLockedChannel(kind, channel, role),
    ),
  );
}
